import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Printer } from 'lucide-react';
import { printUrl } from '../lib/print';

const LightboxContext = createContext(null);

export function useLightbox() {
  const ctx = useContext(LightboxContext);
  if (!ctx) throw new Error('useLightbox must be used within <LightboxProvider>');
  return ctx;
}

export function LightboxProvider({ children }) {
  const [images, setImages] = useState([]); // array of src strings
  const [idx, setIdx] = useState(0);

  const open = useCallback((src, srcs) => {
    const list = (Array.isArray(srcs) && srcs.length > 0) ? srcs : [src];
    const start = Math.max(0, list.indexOf(src));
    setImages(list);
    setIdx(start < 0 ? 0 : start);
  }, []);

  const close = useCallback(() => setImages([]), []);
  const prev = useCallback(() => setIdx((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIdx((i) => (i + 1) % images.length), [images.length]);

  // Keyboard nav
  useEffect(() => {
    if (images.length === 0) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft' && images.length > 1) prev();
      else if (e.key === 'ArrowRight' && images.length > 1) next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [images.length, close, prev, next]);

  const overlay = images.length > 0 && typeof document !== 'undefined' ? createPortal(
    <div
      data-testid="global-lightbox"
      className="fixed inset-0 flex items-center justify-center bg-black/90 p-4 transition-opacity duration-100"
      style={{ zIndex: 10000 }}
      onClick={close}
    >
      {/* X close — iter-73 hitbox upgrade: 48px target, fully opaque
          backdrop, active:scale-90 for instant press feedback. User
          reported the lightbox X felt "slow / unresponsive" — most of
          that was perception (semi-transparent button on busy image
          backdrops) so we boost contrast and feedback. */}
      <button
        data-testid="lightbox-close"
        type="button"
        onClick={(e) => { e.stopPropagation(); close(); }}
        aria-label="Close image"
        className="absolute top-3 right-3 w-12 h-12 rounded-full bg-white text-slate-900 flex items-center justify-center shadow-lg ring-1 ring-black/10 hover:bg-red-50 hover:scale-110 active:scale-90 transition-transform duration-75 cursor-pointer"
        style={{ touchAction: 'manipulation' }}
      >
        <X className="w-6 h-6" strokeWidth={2.5} />
      </button>
      <button
        data-testid="lightbox-print"
        type="button"
        onClick={(e) => { e.stopPropagation(); printUrl(images[idx], 'Image'); }}
        title="Print this image"
        aria-label="Print image"
        className="absolute top-3 right-[72px] w-12 h-12 rounded-full bg-white/95 text-slate-900 flex items-center justify-center shadow-lg hover:bg-white hover:scale-110 active:scale-90 transition-transform duration-75 cursor-pointer"
        style={{ touchAction: 'manipulation' }}
      >
        <Printer className="w-5 h-5" />
      </button>
      {images.length > 1 && (
        <>
          <button
            data-testid="lightbox-prev"
            type="button"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            aria-label="Previous image"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 text-slate-900 flex items-center justify-center shadow-lg hover:bg-white hover:scale-110 active:scale-90 transition-transform duration-75 cursor-pointer"
            style={{ touchAction: 'manipulation' }}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            data-testid="lightbox-next"
            type="button"
            onClick={(e) => { e.stopPropagation(); next(); }}
            aria-label="Next image"
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 text-slate-900 flex items-center justify-center shadow-lg hover:bg-white hover:scale-110 active:scale-90 transition-transform duration-75 cursor-pointer"
            style={{ touchAction: 'manipulation' }}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          <span className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold text-white bg-white/20" style={{ fontFamily: 'Manrope' }}>
            {idx + 1} / {images.length}
          </span>
        </>
      )}
      <img
        src={images[idx]}
        alt="Enlarged"
        className="max-w-full max-h-full object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
      />
    </div>,
    document.body
  ) : null;

  return (
    <LightboxContext.Provider value={{ open, close }}>
      {children}
      {overlay}
    </LightboxContext.Provider>
  );
}
